import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Monitor, Smartphone, Tablet, Globe } from 'lucide-react';
import { fetchAllRows } from '@/lib/supabaseHelpers';

interface Session {
  device_type: string | null;
  is_completed: boolean | null;
  current_step: number | null;
  utm_source: string | null;
}

const DEVICE_ICONS: Record<string, any> = {
  mobile: Smartphone,
  tablet: Tablet,
  desktop: Monitor,
};

const LiveBounceConversionSplit = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pageviews, setPageviews] = useState<{ device_type: string | null; utm_source: string | null }[]>([]);

  const fetchData = async () => {
    const [sessData, pvData] = await Promise.all([
      fetchAllRows<Session>('quiz_sessions', 'device_type, is_completed, current_step, utm_source', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
      fetchAllRows<{ device_type: string | null; utm_source: string | null }>('quiz_pageviews', 'device_type, utm_source', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
    ]);
    setSessions(sessData);
    setPageviews(pvData);
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('live-bounce-conv-split')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  type GroupData = { pageviews: number; started: number; completed: number };

  const groupBy = (key: 'device_type' | 'utm_source') => {
    const groups = new Map<string, GroupData>();

    pageviews.forEach(pv => {
      const k = (key === 'device_type' ? pv.device_type : pv.utm_source) || 'onbekend';
      if (!groups.has(k)) groups.set(k, { pageviews: 0, started: 0, completed: 0 });
      groups.get(k)!.pageviews++;
    });

    sessions.forEach(s => {
      const k = (key === 'device_type' ? s.device_type : s.utm_source) || 'onbekend';
      if (!groups.has(k)) groups.set(k, { pageviews: 0, started: 0, completed: 0 });
      const entry = groups.get(k)!;
      if ((s.current_step ?? 0) >= 2) entry.started++;
      if (s.is_completed) entry.completed++;
    });

    return Array.from(groups.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.pageviews - a.pageviews);
  };

  const deviceData = groupBy('device_type');
  const utmData = groupBy('utm_source');

  const renderTable = (data: ReturnType<typeof groupBy>, type: 'device' | 'utm') => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 text-xs border-b border-gray-100">
            <th className="pb-2 font-medium">{type === 'device' ? 'Apparaat' : 'UTM Bron'}</th>
            <th className="pb-2 font-medium text-right">PV</th>
            <th className="pb-2 font-medium text-right">Bounce</th>
            <th className="pb-2 font-medium text-right">Start</th>
            <th className="pb-2 font-medium text-right">CVR</th>
          </tr>
        </thead>
        <tbody>
          {data.map(d => {
            const bounceRate = d.pageviews > 0 ? Math.round(((d.pageviews - d.started) / d.pageviews) * 100) : 0;
            const startRate = d.pageviews > 0 ? Math.round((d.started / d.pageviews) * 100) : 0;
            const cvr = d.pageviews > 0 ? Math.round((d.completed / d.pageviews) * 100) : 0;
            const Icon = type === 'device' ? (DEVICE_ICONS[d.name] || Monitor) : Globe;
            return (
              <tr key={d.name} className="border-b border-gray-50 last:border-0">
                <td className="py-2 flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="capitalize font-medium text-gray-800">{d.name}</span>
                </td>
                <td className="py-2 text-right text-gray-600">{d.pageviews}</td>
                <td className="py-2 text-right">
                  <span className={bounceRate > 60 ? 'text-red-500 font-medium' : bounceRate < 40 ? 'text-emerald-600 font-medium' : 'text-gray-600'}>
                    {bounceRate}%
                  </span>
                </td>
                <td className="py-2 text-right">
                  <span className={startRate > 60 ? 'text-emerald-600 font-medium' : startRate < 40 ? 'text-orange-500 font-medium' : 'text-gray-600'}>
                    {startRate}%
                  </span>
                </td>
                <td className="py-2 text-right">
                  <span className={cvr > 45 ? 'text-emerald-600 font-bold' : cvr < 25 ? 'text-red-500 font-medium' : 'text-blue-600 font-medium'}>
                    {cvr}%
                  </span>
                </td>
              </tr>
            );
          })}
          {data.length === 0 && (
            <tr><td colSpan={5} className="py-3 text-gray-400 text-center">Nog geen data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
        📊 Bounce & Conversie uitsplitsing
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <p className="text-xs text-gray-400 mb-4">Benchmark: Bounce 40-60% • Start 40-60% • CVR 25-45% (lange quiz)</p>

      <div className="space-y-5">
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Per apparaat</h4>
          {renderTable(deviceData, 'device')}
        </div>
        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Per UTM-bron</h4>
          {renderTable(utmData, 'utm')}
        </div>
      </div>
    </div>
  );
};

export default LiveBounceConversionSplit;
